"use strict";
/* globals module */
/**
 * @module articleInstanceFilesClient
 * @description  Article Instance Files Client provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Files Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
*/
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../../httpApi");
var __1 = require("../");
var ArticleInstanceFilesClient = /** @class */ (function () {
    function ArticleInstanceFilesClient(articleInstanceFilesRoute, articleInstanceFilesStreamsClient, ArticleInstanceFilesBatchClient, apiClient) {
        this.articleInstanceFilesRoute = articleInstanceFilesRoute;
        this.articleInstanceFilesStreamsClient = articleInstanceFilesStreamsClient;
        this.ArticleInstanceFilesBatchClient = ArticleInstanceFilesBatchClient;
        this.apiClient = apiClient;
    }
    Object.defineProperty(ArticleInstanceFilesClient.prototype, "routeDefinition", {
        get: function () {
            return this.articleInstanceFilesRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleInstanceFilesClient.prototype, "streams", {
        get: function () {
            return this.articleInstanceFilesStreamsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ArticleInstanceFilesClient.prototype, "batch", {
        get: function () {
            return this.ArticleInstanceFilesBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of file resources matching the given criteria.
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose article files need to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example articleInstanceFilesClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    search : '<search-phrase>'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                    // perform error handling here
                });
     **/
    ArticleInstanceFilesClient.prototype.find = function (articleId, options) {
        return this.apiClient.get(this.articleInstanceFilesRoute.find(articleId, options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns requested file resource.
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose article files need to be retrieved.
     * @param id Article file id which uniquely identifies article file that needs to be retrieved.
     * @param options options object that contains embed data.
     * @returns A promise that is resolved once the get action has been performed.
     * @example articleInstanceFilesClient.get('<file-id>')
                   .then(function (data) {
                       // perform success action here
                   },
                    function (response, status, headers, config) {
                       // perform error handling here
                   });
    **/
    ArticleInstanceFilesClient.prototype.get = function (articleId, id, options) {
        return this.apiClient.get(this.articleInstanceFilesRoute.get(articleId, id, options));
    };
    /**
     * Returns a promise that is resolved once the unlink action has been performed. This action will remove one or many file resources from the system if successfully completed. Specified file and all its accompanying derived resources will be removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicArticleFilesRouteService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(fileEntry);
     * let uri = params['model'].links('unlink').href;
     * ```
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose article files need to be deleted.
     * @param data
     * @param options
     * @returns A promise that is resolved once the unlink action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action. The following action will remove the original file resource and all accompanying derived file resources.
                    articleInstanceFilesRoute.remove(fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                            // perform error handling here
                        });
     **/
    ArticleInstanceFilesClient.prototype.unlink = function (articleId, data, options) {
        return this.apiClient.delete(this.articleInstanceFilesRoute.unlink(articleId, data, options));
    };
    /**
     * Returns a promise that is resolved once the unlink by article action has been performed. This action will remove all file resources from the system related to the requested article if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicArticleService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(fileEntry);
     * let uri = params['model'].links('unlink-by-article').href;
     * ```
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose article files need to be deleted.
     * @param data
     * @param options
     * @returns A promise that is resolved once the unlink by article action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action.
                    articleInstanceFilesClient.unlinkByArticle(fileEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    ArticleInstanceFilesClient.prototype.unlinkByArticle = function (articleId, data, options) {
        return this.apiClient.delete(this.articleInstanceFilesRoute.unlink(articleId, data, options));
    };
    /**
     * Returns a promise that is resolved once the update file action has been performed; this action will update a file resource if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `baasicArticleFilesRouteService` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     ```
     let params = modelMapper.updateParams(fileEntry);
     let uri = params['model'].links('put').href;
     ```
     * @method
     * @param articleId An article object used to update specified article resource.
     * @param data Article File object used to update specific article file data in the system.
     * @returns A promise that is resolved once the update file action has been performed.
     * @example // fileEntry is a file resource previously fetched using get action.
                   fileEntry.description = '<description>';
                   articleInstanceFilesClient.update(fileEntry)
                       .then(function (data) {
                           // perform success action here
                       },
                        function (response, status, headers, config) {
                           // perform error handling here
                       });
    **/
    ArticleInstanceFilesClient.prototype.update = function (articleId, data) {
        return this.apiClient.put(this.routeDefinition.update(articleId, data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the link action has been performed; this action links file resource from other modules into the Article Files module (For example: file resources from the Media Vault module can be linked directly into the Article Files module).
     * @method
     * @param articleId Article slug or id which uniquely identifies article whose article files need to be linked.
     * @param data A article file object that need to be inserted into the system.
     * @returns A promise that is resolved once the link action has been performed.
     * @example articleInstanceFilesClient.link(fileObject)
                    .then(function (response, status, headers, config) {
                        // perform success handling here
                    },
                        function (response, status, headers, config) {
                        // perform error handling here
                    });
     **/
    ArticleInstanceFilesClient.prototype.link = function (articleId, data) {
        return this.apiClient.post(this.routeDefinition.link(articleId, data), this.routeDefinition.createParams(data));
    };
    var _a, _b, _c, _d;
    ArticleInstanceFilesClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(__1.TYPES.ArticleInstanceFilesRoute)),
        tslib_1.__param(1, inversify_1.inject(__1.TYPES.ArticleInstanceFilesStreamsClient)),
        tslib_1.__param(2, inversify_1.inject(__1.TYPES.ArticleInstanceFilesBatchClient)),
        tslib_1.__param(3, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof __1.ArticleInstanceFilesRoute !== "undefined" && __1.ArticleInstanceFilesRoute) === "function" ? _a : Object, typeof (_b = typeof __1.ArticleInstanceFilesStreamsClient !== "undefined" && __1.ArticleInstanceFilesStreamsClient) === "function" ? _b : Object, typeof (_c = typeof __1.ArticleInstanceFilesBatchClient !== "undefined" && __1.ArticleInstanceFilesBatchClient) === "function" ? _c : Object, typeof (_d = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _d : Object])
    ], ArticleInstanceFilesClient);
    return ArticleInstanceFilesClient;
}());
exports.ArticleInstanceFilesClient = ArticleInstanceFilesClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
*/ 
